/**
 * TemplateGallery Component
 * Phase 2 Sprint 13: Template Selection UI
 * Enhanced with Wizard-based Entry Points
 * 
 * Displays template library with category filtering and wizard flows
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Tabs, Empty, Spin, message, Button, Modal, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Sparkles, Wand2, Settings, Eye } from 'lucide-react';
import { useSetAtom } from 'jotai';
import { Template, TemplateCategory } from '../../../../types/template';
import { templateService } from '../../services/templateService';
import { projectsManager } from '../../services/projectsManager';
import { TEMPLATE_CATEGORIES } from '../../../../data/templateSeeds';
import { TemplatePreviewModal } from './TemplatePreviewModal';
import { TemplateCustomizer } from './TemplateCustomizer';
import { MakeItYoursWizard, SmartTemplateWizard } from './wizards';
import { useTemplateLoader } from '../../hooks/useTemplateLoader';
import { useFeatureFlag } from '../../services/featureFlags';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { UpgradePrompt } from '../overlays/UpgradePrompt';
import { initMakeItYoursAtom, wizardViewAtom } from '../../atoms/wizardAtoms';
import './TemplateGallery.scss';


type ModalType = 'preview' | 'customizer';

interface ModalStackEntry {
  type: ModalType;
  template?: Template;
  customizerMode?: 'create' | 'edit' | 'duplicate';
}

interface TemplateGalleryProps {
  onTemplateSelect?: (template: Template) => void;
  onParentModalVisibility?: (visible: boolean) => void;
}

export const TemplateGallery: React.FC<TemplateGalleryProps> = ({
  onTemplateSelect,
  onParentModalVisibility
}) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [customTemplates, setCustomTemplates] = useState<Template[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<TemplateCategory | 'all' | 'custom'>('all');
  
  const [modalStack, setModalStack] = useState<ModalStackEntry[]>([]);
  
  const ITEMS_PER_PAGE = 6;
  const [displayCount, setDisplayCount] = useState(ITEMS_PER_PAGE);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  
  const { loadTemplate } = useTemplateLoader();
  const isCustomTemplatesEnabled = useFeatureFlag('custom-templates');
  const initMakeItYours = useSetAtom(initMakeItYoursAtom);
  const { isPaid } = useFeatureAccess();
  
  const [showMakeItYoursWizard, setShowMakeItYoursWizard] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [showSmartWizard, setShowSmartWizard] = useState(false);
  const [selectedTemplateForWizard, setSelectedTemplateForWizard] = useState<Template | null>(null);

  const topModal = modalStack.length > 0 ? modalStack[modalStack.length - 1] : null;
  const previewEntry = modalStack.find(m => m.type === 'preview');
  const customizerEntry = modalStack.find(m => m.type === 'customizer');
  const hasOpenModal = modalStack.length > 0 || showSmartWizard || showMakeItYoursWizard;
  
  useEffect(() => {
    onParentModalVisibility?.(!hasOpenModal);
  }, [hasOpenModal, onParentModalVisibility]);
  
  const pushModal = (entry: ModalStackEntry) => {
    setModalStack(prev => [...prev, entry]);
  };
  
  const popModal = () => {
    setModalStack(prev => prev.slice(0, -1));
  };
  
  const closeAllModals = () => {
    setModalStack([]);
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    // Custom templates first, then official templates
    const allTemplates = [...customTemplates, ...templates];
    if (selectedCategory === 'all') {
      setFilteredTemplates(allTemplates);
    } else if (selectedCategory === 'custom') {
      setFilteredTemplates(customTemplates);
    } else {
      setFilteredTemplates(
        allTemplates.filter(t => t.category === selectedCategory)
      );
    }
    setDisplayCount(ITEMS_PER_PAGE);
  }, [selectedCategory, templates, customTemplates]);

  const loadMoreTemplates = useCallback(() => {
    if (loadingMore || displayCount >= filteredTemplates.length) return;
    
    setLoadingMore(true);
    setTimeout(() => {
      setDisplayCount(prev => Math.min(prev + ITEMS_PER_PAGE, filteredTemplates.length));
      setLoadingMore(false);
    }, 300);
  }, [loadingMore, displayCount, filteredTemplates.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && displayCount < filteredTemplates.length) {
          loadMoreTemplates();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [loadMoreTemplates, loadingMore, displayCount, filteredTemplates.length]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      
      // Force re-seed official templates to get latest metadata
      // This ensures templates have updated notes and tags from templateSeeds
      try {
        await templateService.reseedOfficialTemplates();
        console.log('✅ Templates refreshed with latest metadata');
      } catch (reseedError) {
        console.warn('Template reseed failed, using existing templates:', reseedError);
      }
      
      // One-time migration: sync existing IDB custom templates to backend
      // This ensures cross-device access for users with local templates
      try {
        const syncResult = await templateService.syncIDBToBackend();
        if (syncResult.synced > 0) {
          console.log(`✅ Synced ${syncResult.synced} templates to backend for cross-device access`);
        }
      } catch (syncError) {
        console.warn('Template sync to backend failed:', syncError);
      }
      
      const [allTemplates, userCustomTemplates] = await Promise.all([
        templateService.getAllTemplates(),
        templateService.getCustomTemplates()
      ]);
      setTemplates(allTemplates);
      setCustomTemplates(userCustomTemplates);
    } catch (error) {
      message.error('Failed to load templates');
      console.error('Template load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewTemplate = (template: Template, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    pushModal({ type: 'preview', template });
  };

  const handleClosePreview = () => {
    popModal();
  };

  const handleUseTemplate = async (template: Template) => {
    try {
      await loadTemplate(template);
      message.success(`Template "${template.title}" loaded successfully!`);
      closeAllModals();
      
      if (onTemplateSelect) {
        onTemplateSelect(template);
      }
    } catch (error) {
      message.error('Failed to load template');
      console.error('Template load error:', error);
    }
  };

  const handleCustomizeTemplate = (template: Template) => {
    pushModal({ 
      type: 'customizer', 
      template, 
      customizerMode: template.isOfficial ? 'duplicate' : 'edit' 
    });
  };

  const handleCreateNewTemplate = () => {
    pushModal({ type: 'customizer', customizerMode: 'create' });
  };

  const handleMakeItYours = (template: Template, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    
    // Gate feature for non-paid users
    if (!isPaid) {
      setShowUpgradePrompt(true);
      return;
    }
    
    initMakeItYours(template);
    setSelectedTemplateForWizard(template);
    setShowMakeItYoursWizard(true);
  };

  const handleOpenSmartWizard = () => {
    setShowSmartWizard(true);
  };

  const handleStartBlank = async () => {
    try {
      const newProject = await projectsManager.createProject({
        name: 'Untitled Project',
        description: 'Blank project',
        aspectRatio: '16:9'
      });

      await projectsManager.createScene({
        projectId: newProject.id,
        title: 'Scene 1',
        description: '',
        videoUrl: undefined,
        duration: 0,
        sceneOrder: 0,
        metadata: {}
      });

      closeAllModals();
      navigate(`/recorder?mode=project&projectId=${newProject.id}`);
    } catch (error) {
      console.error('Failed to create blank project:', error);
      message.error('Failed to create project. Please try again.');
    }
  };

  const handleCloseWizards = () => {
    setShowMakeItYoursWizard(false);
    setShowSmartWizard(false);
    setSelectedTemplateForWizard(null);
  };

  const handleWizardComplete = (projectId: number) => {
    handleCloseWizards();
    closeAllModals();
    navigate(`/recorder?mode=project&projectId=${projectId}`);
  };

  const handleCloseCustomizer = () => {
    popModal();
    loadTemplates();
  };

  const handleDeleteTemplate = (template: Template) => {
    Modal.confirm({
      title: 'Delete Custom Template',
      content: `Are you sure you want to delete "${template.title}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await templateService.deleteCustomTemplate(template.id);
          message.success('Template deleted successfully');
          closeAllModals();
          loadTemplates();
        } catch (error) {
          message.error('Failed to delete template');
          console.error('Template delete error:', error);
        }
      }
    });
  };

  const getCategoryIcon = (category: TemplateCategory | 'all' | 'custom') => {
    if (category === 'all') return '🎬';
    if (category === 'custom') return '⭐';
    return TEMPLATE_CATEGORIES[category as TemplateCategory]?.icon || '📁';
  };

  // Template ID to thumbnail image mapping
  const getTemplateThumbnail = (templateId: string): string | null => {
    const thumbnailMap: Record<string, string> = {
      'edu-course-intro': '/templates/course-intro.jpeg',
      'edu-tutorial': '/templates/tutorial-explainer.jpeg',
      'edu-student-testimonial': '/templates/student-testimonial.jpeg',
      'biz-pitch-deck': '/templates/pitch-deck.jpeg',
      'biz-product-demo': '/templates/product-demo.jpeg',
      'biz-company-culture': '/templates/company-culture.jpeg',
      'content-youtube-opener': '/templates/youtube-opener.jpeg',
      'content-social-promo': '/templates/social-promo.jpeg',
      'dev-code-tutorial': '/templates/code-tutorial.jpeg',
      'dev-api-docs': '/templates/api-doc.jpeg',
      'interview-intro': '/templates/candidate-intro.jpeg',
      'interview-remote-setup': '/templates/remote-interview.jpeg',
    };
    return thumbnailMap[templateId] || null;
  };

  const getCategoryLabel = (category: TemplateCategory | 'all' | 'custom') => {
    if (category === 'all') return 'All Templates';
    if (category === 'custom') return 'Custom';
    return TEMPLATE_CATEGORIES[category as TemplateCategory]?.label || category;
  };

  const renderTemplateCard = (template: Template) => {
    const sceneCount = template.scenes.length;
    const totalDuration = template.scenes.reduce(
      (sum, scene) => sum + scene.suggestedDuration,
      0
    );
    const minutes = Math.floor(totalDuration / 60);
    const seconds = totalDuration % 60;
    const thumbnail = getTemplateThumbnail(template.id);
    const customPlaceholder = '/templates/custom.jpeg';

    return (
      <Card
        key={template.id}
        className="template-card"
        hoverable
      >
        {/* Thumbnail Section */}
        <div className="template-thumbnail">
          <img 
            src={thumbnail || customPlaceholder} 
            alt={template.title}
            loading="lazy"
          />
          <div className="thumbnail-overlay">
            <Button
              type="primary"
              icon={<Eye size={16} />}
              onClick={(e) => handlePreviewTemplate(template, e)}
              className="preview-btn"
            >
              Preview
            </Button>
          </div>
          <div className="thumbnail-badge-top">
            {template.isOfficial ? (
              <span className="template-badge official">Official</span>
            ) : (
              <span className="template-badge custom">Custom</span>
            )}
          </div>
          <div className="thumbnail-badge-bottom">
            <span className="info-badge">
              {sceneCount} Scenes • {minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`}
            </span>
          </div>
        </div>

        {/* Content Section */}
        <div className="template-content">
          <div className="template-card-header">
            <h3 className="template-title">{template.title}</h3>
            <span className="category-tag">
              {getCategoryIcon(template.category)} {getCategoryLabel(template.category)}
            </span>
          </div>
          
          <p className="template-description">{template.description}</p>

          {template.aspectRatio && (
            <div className="template-meta">
              <Tooltip 
                title={
                  template.aspectRatio === '16:9' 
                    ? 'Best for: YouTube, Courses, Presentations' 
                    : template.aspectRatio === '9:16'
                    ? 'Best for: Instagram Reels, TikTok, YouTube Shorts'
                    : template.aspectRatio === '1:1'
                    ? 'Best for: Instagram Feed, LinkedIn'
                    : 'Standard aspect ratio'
                }
              >
                <span className="aspect-badge">
                  <span className={`aspect-icon aspect-icon--${template.aspectRatio.replace(':', '-')}`} />
                  {template.aspectRatio}
                </span>
              </Tooltip>
            </div>
          )}

          <div className="template-actions">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleUseTemplate(template);
              }}
              className={template.isOfficial ? "use-btn-secondary" : "use-btn-primary"}
              type={template.isOfficial ? "default" : "primary"}
            >
              Use Template
            </Button>
            {template.isOfficial && (
              <Button
                type="primary"
                onClick={(e) => handleMakeItYours(template, e)}
                className="make-it-yours-btn"
              >
                <Sparkles size={14} />
                Make It Yours
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  };

  const renderSkeletonCard = (index: number) => (
    <div key={index} className="template-card-skeleton">
      <div className="skeleton-thumbnail" />
      <div className="skeleton-content">
        <div className="skeleton-title" />
        <div className="skeleton-category" />
        <div className="skeleton-description" />
        <div className="skeleton-description short" />
        <div className="skeleton-actions">
          <div className="skeleton-button" />
          <div className="skeleton-button primary" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="template-gallery">
        <div className="gallery-header">
          <div className="header-title-section">
            <div className="skeleton-header-title" />
            <div className="skeleton-header-subtitle" />
          </div>
          <div className="header-actions">
            <div className="skeleton-header-button" />
            <div className="skeleton-header-button" />
            <div className="skeleton-header-button primary" />
          </div>
        </div>

        <div className="skeleton-tabs">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="skeleton-tab" />
          ))}
        </div>

        <div className="template-grid">
          {[1, 2, 3, 4, 5, 6].map(i => renderSkeletonCard(i))}
        </div>
      </div>
    );
  }

  return (
    <div className="template-gallery">
      <div className="gallery-header">
        <div className="header-title-section">
          <h2>Template Library</h2>
          <p className="gallery-subtitle">
            Pick a template and start creating in minutes
          </p>
        </div>
        <div className="header-actions">
          <Tooltip title="Start with a blank canvas - no scripts, no presets">
            <Button
              onClick={handleStartBlank}
              className="start-blank-btn"
            >
              Start Blank
            </Button>
          </Tooltip>
          <Button
            onClick={handleOpenSmartWizard}
            className="smart-wizard-btn"
            icon={<Wand2 size={16} />}
          >
            Help Me Choose
          </Button>
          {isCustomTemplatesEnabled && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateNewTemplate}
            >
              Create Custom
            </Button>
          )}
        </div>
      </div>

      <Tabs
        activeKey={selectedCategory}
        onChange={(key) => setSelectedCategory(key as TemplateCategory | 'all' | 'custom')}
        className="category-tabs"
        items={[
          {
            key: 'all',
            label: `${getCategoryIcon('all')} All Templates (${templates.length + customTemplates.length})`,
          },
          ...(customTemplates.length > 0 ? [{
            key: 'custom',
            label: `${getCategoryIcon('custom')} Custom (${customTemplates.length})`,
          }] : []),
          ...Object.entries(TEMPLATE_CATEGORIES).map(([key, { label, icon, count }]) => ({
            key,
            label: `${icon} ${label} (${count})`,
          })),
        ]}
      />

      <div className="template-grid">
        {filteredTemplates.length === 0 ? (
          <Empty 
            description="No templates in this category yet. Check back soon!" 
            className="empty-state"
          />
        ) : (
          filteredTemplates.slice(0, displayCount).map(template => renderTemplateCard(template))
        )}
      </div>
      
      {displayCount < filteredTemplates.length && (
        <div ref={loadMoreRef} className="load-more-trigger">
          {loadingMore && <Spin size="small" />}
        </div>
      )}

      <TemplatePreviewModal
        template={previewEntry?.template || null}
        visible={topModal?.type === 'preview'}
        onClose={handleClosePreview}
        onUseTemplate={handleUseTemplate}
        onMakeItYours={handleMakeItYours}
        onCustomizeTemplate={handleCustomizeTemplate}
        onDeleteTemplate={handleDeleteTemplate}
      />

      <TemplateCustomizer
        visible={topModal?.type === 'customizer'}
        onClose={handleCloseCustomizer}
        mode={customizerEntry?.customizerMode || 'create'}
        template={customizerEntry?.template}
        onSaveSuccess={() => {
          loadTemplates();
        }}
      />

      <div className="create-your-own-section">
        <h3>Create Your Own</h3>
        <div className="create-options">
          <div className="create-option smart-option" onClick={handleOpenSmartWizard}>
            <div className="option-icon-wrapper">
              <Wand2 size={24} />
            </div>
            <div className="option-content">
              <h4>Smart Create</h4>
              <p>Answer a few questions and we'll set up the perfect template for you</p>
            </div>
            <Button type="primary" className="option-btn">Start Wizard</Button>
          </div>
          
          {isCustomTemplatesEnabled && (
            <div className="create-option advanced-option" onClick={handleCreateNewTemplate}>
              <div className="option-icon-wrapper">
                <Settings size={24} />
              </div>
              <div className="option-content">
                <h4>Advanced</h4>
                <p>Build from scratch with full control over every detail</p>
              </div>
              <Button className="option-btn">Open Editor</Button>
            </div>
          )}
        </div>
      </div>

      <MakeItYoursWizard
        visible={showMakeItYoursWizard}
        onClose={handleCloseWizards}
        onComplete={handleWizardComplete}
      />

      <SmartTemplateWizard
        visible={showSmartWizard}
        onClose={handleCloseWizards}
        onSelectTemplate={() => {
          handleCloseWizards();
          if (onTemplateSelect && selectedTemplateForWizard) {
            onTemplateSelect(selectedTemplateForWizard);
          }
        }}
        onMakeItYours={() => {
          setShowSmartWizard(false);
          setShowMakeItYoursWizard(true);
        }}
      />
      
      <UpgradePrompt
        visible={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        feature="AI-Guided Personalization"
        description="Make It Yours uses AI to personalize templates based on your needs. Upgrade to Pro to unlock this feature and create custom templates tailored to your content."
      />
    </div>
  );
};

export default TemplateGallery;
